import React, { useEffect, useState } from "react";
import { dateTimeInput } from "@/lib/timezones";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { teamLabel } from "@/components/manage/matchUtils";
import ScheduleTimeRange, { ScheduleBreak } from "@/components/manage/ScheduleTimeRange";
import { buildMatchBreaks } from "@/components/manage/scheduleTimeline";
import ScheduleGrid from "@/components/manage/ScheduleGrid";
import { CalendarClock, AlertTriangle, Wand2, LayoutGrid, List } from "lucide-react";

export default function ManageSchedule({ data, refresh, teamMap, refMap, warnings }) {
  const { tournament: t, matches, referees } = data;
  const flds = t.fields || [];
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("grid");
  const [fieldNames, setFieldNames] = useState({});
  const [autoOpen, setAutoOpen] = useState(false);
  const [firstStart, setFirstStart] = useState("");
  const [editingMatch, setEditingMatch] = useState(null);
  const [matchStart, setMatchStart] = useState("");
  const [savingTime, setSavingTime] = useState(false);

  useEffect(() => {
    setFieldNames(Object.fromEntries((t.fields || []).map((f) => [f.id, f.name || ""])));
  }, [t.fields]);

  const scheduleValue = (value) => t.timezone ? value : new Date(value).toISOString();

  const openAutoSchedule = () => {
    const first = matches.filter((m) => m.scheduled_time).sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))[0];
    const configured = t.start_time;
    const fallback = configured
      ? (/(Z|[+-]\d{2}:\d{2})$/.test(configured) ? dateTimeInput(configured, t.timezone) : configured.slice(0, 16))
      : t.date ? `${t.date}T09:00` : dateTimeInput(new Date().toISOString(), t.timezone);
    setFirstStart(first ? dateTimeInput(first.scheduled_time, t.timezone) : fallback);
    setAutoOpen(true);
  };

  const editTime = (match) => {
    setMatchStart(dateTimeInput(match.scheduled_time, t.timezone));
    setEditingMatch(match.id);
  };

  const saveMatchTime = async (event) => {
    event.preventDefault();
    if (!matchStart || !editingMatch) return;
    setSavingTime(true);
    try {
      await api.put(`/matches/${editingMatch}`, { scheduled_time: scheduleValue(matchStart) });
      setEditingMatch(null);
      toast.success("Match start time updated");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingTime(false); }
  };

  const regen = async (event) => {
    event.preventDefault();
    if (!firstStart) return;
    setBusy(true);
    try {
      await api.post(`/tournaments/${t.id}/schedule`, { start_time: scheduleValue(firstStart) });
      setAutoOpen(false);
      toast.success("Schedule regenerated");
      await refresh();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const patch = async (id, body) => {
    try { await api.put(`/matches/${id}`, body); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveFields = async () => {
    const fields = flds.map((f, i) => ({ ...f, name: (fieldNames[f.id] || "").trim() || `Field ${i + 1}` }));
    try { await api.put(`/tournaments/${t.id}/fields`, { fields }); toast.success("Field names saved"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };


  const warnIds = new Set(warnings.flatMap((w) => w.ids));
  const sorted = [...matches].sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  const breaks = buildMatchBreaks(matches, t);
  const timeline = [
    ...sorted.map((match) => ({ id: match.id, time: match.scheduled_time, match })),
    ...[...breaks.values()].map((pause) => ({ id: pause.id, time: pause.start, pause })),
  ].sort((a, b) => (new Date(a.time).getTime() || 0) - (new Date(b.time).getTime() || 0));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Schedule</h2>
          <p className="text-sm text-muted-foreground">{matches.length} matches · {flds.length} fields · {t.match_duration}min + {t.break_time}min break</p>
          <p className="text-xs text-muted-foreground">Timezone: {t.timezone || "Browser local (not configured)"}</p>
        </div>
        <div className="flex gap-2">
          <div className="flex rounded-lg border p-0.5">
            <button onClick={() => setView("grid")} data-testid="schedule-view-grid"
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-sm font-medium ${view === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <LayoutGrid className="h-4 w-4" /> Grid
            </button>
            <button onClick={() => setView("list")} data-testid="schedule-view-list"
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-sm font-medium ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <List className="h-4 w-4" /> List
            </button>
          </div>
          <Button variant="outline" onClick={openAutoSchedule} disabled={busy || !matches.length} data-testid="regenerate-schedule">
            <Wand2 className="mr-1.5 h-4 w-4" /> Auto-schedule
          </Button>
        </div>
      </div>

      {flds.length > 0 && (
        <Card className="space-y-3 p-4">
          <div>
            <h3 className="font-heading font-bold">Fields</h3>
            <p className="text-sm text-muted-foreground">Rename fields here. The new names appear in schedules, public views, captain/team pages and referee views.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {flds.map((f, i) => (
              <div key={f.id}>
                <label className="text-xs font-medium text-muted-foreground">Field {i + 1}</label>
                <Input
                  value={fieldNames[f.id] ?? f.name ?? ""}
                  onChange={(e) => setFieldNames((cur) => ({ ...cur, [f.id]: e.target.value }))}
                  onBlur={saveFields}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  className="mt-1.5"
                  data-testid={`field-name-${f.id}`}
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      {view === "grid" && matches.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground">Click a match's start time to choose a date and time, or drag its grip handle onto another field or time slot. Finalized matches are locked.</p>
          <ScheduleGrid tournament={t} matches={matches} referees={referees} teamMap={teamMap}
            warnIds={warnIds}
            onEditTime={editTime}
            onMove={(id, body) => patch(id, body)}
            onSetReferee={(id, ref) => patch(id, { referee_id: ref })} />
        </>
      )}

      {warnings.length > 0 && (
        <Card className="border-accent/30 bg-accent/5 p-4">
          <div className="flex items-center gap-2 font-semibold text-accent"><AlertTriangle className="h-4 w-4" /> {warnings.length} scheduling conflict{warnings.length > 1 ? "s" : ""}</div>
          <ul className="mt-2 space-y-1 text-sm text-accent">
            {warnings.slice(0, 8).map((w, i) => <li key={i}>• {w.message}</li>)}
          </ul>
        </Card>
      )}

      {matches.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">Generate the tournament structure first to build a schedule.</Card>
      ) : view === "list" ? (
        <div className="space-y-2">
          {timeline.map((entry) => {
            if (entry.pause) return <ScheduleBreak key={entry.id} pause={entry.pause} tournament={t} showField />;
            const m = entry.match;
            return (
            <Card key={m.id} className={`p-3 ${warnIds.has(m.id) ? "border-accent/50" : ""}`} data-testid={`schedule-row-${m.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[180px] flex-1">
                  <div className="text-xs text-muted-foreground">{m.round_name}{m.status === "final" ? " · FINAL" : ""}</div>
                  <div className="font-medium">{teamLabel(m, "a", teamMap)} <span className="text-muted-foreground">vs</span> {teamLabel(m, "b", teamMap)}</div>
                </div>
                <Button variant="outline" className="h-auto py-2" disabled={m.status === "final"} onClick={() => editTime(m)}
                  aria-label={`Change start time for ${teamLabel(m, "a", teamMap)} vs ${teamLabel(m, "b", teamMap)}`}
                  data-testid={`edit-time-${m.id}`}>
                  <CalendarClock className="mr-1.5 h-4 w-4" /> <ScheduleTimeRange match={m} tournament={t} />
                </Button>
                <Select value={m.field_id || ""} disabled={m.status === "final"} onValueChange={(v) => patch(m.id, { field_id: v })}>
                  <SelectTrigger className="w-[130px]" data-testid={`field-select-${m.id}`}><SelectValue placeholder="Field" /></SelectTrigger>
                  <SelectContent>{flds.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={m.referee_id || "none"} disabled={m.status === "final"} onValueChange={(v) => patch(m.id, { referee_id: v === "none" ? null : v })}>
                  <SelectTrigger className="w-[140px]" data-testid={`ref-select-${m.id}`}><SelectValue placeholder="Referee" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No referee</SelectItem>
                    {referees.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </Card>
            );
          })}
        </div>
      ) : null}
      <Dialog open={autoOpen} onOpenChange={(open) => !busy && setAutoOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Auto-schedule matches</DialogTitle>
            <DialogDescription>When should the first match start? All match times will be recalculated using the match duration, breaks and fields. Timezone: {t.timezone || "Browser local"}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={regen} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="first-match-start">First match start</Label>
              <Input id="first-match-start" type="datetime-local" required value={firstStart} disabled={busy}
                onChange={(e) => setFirstStart(e.target.value)} data-testid="auto-schedule-start" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={busy} onClick={() => setAutoOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || !firstStart} data-testid="confirm-auto-schedule">{busy ? "Scheduling…" : "Auto-schedule"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingMatch} onOpenChange={(open) => !open && !savingTime && setEditingMatch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change match start time</DialogTitle>
            <DialogDescription>Choose the date and time in {t.timezone || "your browser's local timezone"}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveMatchTime} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="match-start-time">Match start</Label>
              <Input id="match-start-time" type="datetime-local" required value={matchStart} disabled={savingTime}
                onChange={(e) => setMatchStart(e.target.value)} data-testid="match-start-picker" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={savingTime} onClick={() => setEditingMatch(null)}>Cancel</Button>
              <Button type="submit" disabled={savingTime || !matchStart} data-testid="save-match-start">{savingTime ? "Saving…" : "Save start time"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
